import { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useOrgStore } from '../../shared/store/org.store.js'
import { getLocation, suspendLocation, activateLocation, archiveLocation } from '../../shared/api/locations.js'
import { getCities, type CityRef } from '../../shared/api/cities.js'
import type { Location } from '../../shared/types/location.js'
import { canManageCatalog } from '../../shared/utils/permissions.js'
import type { LifecycleAction } from '../../shared/utils/catalog.js'
import { ApiError } from '../../shared/types/api.js'
import { ROUTES } from '../../app/routes.js'
import StatusBadge from '../menu/components/StatusBadge.js'
import LifecycleActions from '../menu/components/LifecycleActions.js'

type DetailStatus = 'loading' | 'ready' | 'error' | 'notFound'

export default function LocationDetailPage() {
  const { t } = useTranslation()
  const { locationId } = useParams<{ locationId: string }>()
  const navigate = useNavigate()

  const orgId = useOrgStore(s => s.activeOrganization)?.id
  const role = useOrgStore(s => s.activeOrganization)?.role
  const canManage = canManageCatalog(role)

  const [location, setLocation] = useState<Location | null>(null)
  const [status, setStatus] = useState<DetailStatus>('loading')
  const [cities, setCities] = useState<CityRef[]>([])

  useEffect(() => {
    getCities().then(setCities).catch(() => setCities([]))
  }, [])
  const [actionError, setActionError] = useState('')

  async function load() {
    if (!orgId || !locationId) return
    setStatus('loading')
    try {
      const data = await getLocation(orgId, locationId)
      setLocation(data)
      setStatus('ready')
    } catch (err: unknown) {
      setStatus(err instanceof ApiError && err.status === 404 ? 'notFound' : 'error')
    }
  }

  useEffect(() => {
    void load()
  }, [orgId, locationId])

  async function handleLifecycle(action: LifecycleAction) {
    if (!orgId || !location) return
    setActionError('')
    try {
      const updated =
        action === 'suspend'
          ? await suspendLocation(orgId, location.id)
          : action === 'activate'
            ? await activateLocation(orgId, location.id)
            : await archiveLocation(orgId, location.id)
      setLocation(updated)
    } catch {
      setActionError(t('locations.lifecycleError'))
    }
  }

  if (status === 'loading') {
    return (
      <section className="page-section">
        <h2>{t('app.locations')}</h2>
        <div className="page-loading">
          <div className="spinner" />
        </div>
      </section>
    )
  }

  if (status === 'notFound' || status === 'error' || !location) {
    return (
      <section className="page-section">
        <div className="page-header">
          <Link to={ROUTES.LOCATIONS} className="back-link">
            ← {t('locations.back')}
          </Link>
        </div>
        <div className="settings-card">
          <div className="form-error-banner">
            {status === 'notFound' ? t('locations.notFound') : t('locations.loadError')}
          </div>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              if (status === 'notFound') navigate(ROUTES.LOCATIONS)
              else void load()
            }}
          >
            {t('error.retry')}
          </button>
        </div>
      </section>
    )
  }

  return (
    <section className="page-section page-section--wide">
      <Link to={ROUTES.LOCATIONS} className="back-link">
        ← {t('locations.back')}
      </Link>

      <div className="page-header menu-detail-header">
        <div>
          <h2>{location.name}</h2>
          <p className="menu-detail-slug">{location.slug ?? '—'}</p>
        </div>
        <StatusBadge status={location.status as 'ACTIVE' | 'SUSPENDED' | 'ARCHIVED'} />
      </div>

      <div className="settings-card">
        <table className="location-info-table">
          <tbody>
            {location.description && (
              <tr><td className="info-label">{t('locations.description')}</td><td>{location.description}</td></tr>
            )}
            {location.address && (
              <tr><td className="info-label">{t('locations.address')}</td><td>{location.address}</td></tr>
            )}
            {location.phone && (
              <tr><td className="info-label">{t('restaurant.phone')}</td><td>{location.phone}</td></tr>
            )}
            {location.email && (
              <tr><td className="info-label">{t('locations.email')}</td><td>{location.email}</td></tr>
            )}
            {location.cityId && (
              <tr>
                <td className="info-label">{t('locations.city')}</td>
                <td>{cities.find(c => c.id === location.cityId)?.name ?? '—'}</td>
              </tr>
            )}
            <tr><td className="info-label">{t('locations.timezone')}</td><td>{location.timezone}</td></tr>
            <tr><td className="info-label">{t('locations.currency')}</td><td>{location.currency}</td></tr>
            <tr><td className="info-label">{t('locations.locale')}</td><td>{location.locale}</td></tr>
            {location.latitude !== null && (
              <tr><td className="info-label">{t('locations.latitude')}</td><td>{location.latitude}</td></tr>
            )}
            {location.longitude !== null && (
              <tr><td className="info-label">{t('locations.longitude')}</td><td>{location.longitude}</td></tr>
            )}
          </tbody>
        </table>

        {canManage && location.status !== 'ARCHIVED' && (
          <div className="form-actions" style={{ display: 'flex', gap: 8 }}>
            <a href={`${ROUTES.LOCATIONS}/${location.id}/edit`} className="btn btn-primary" role="button">
              {t('locations.edit')}
            </a>
            <a href={`${ROUTES.LOCATIONS}/${location.id}/delivery-zones`} className="btn btn-secondary" role="button">
              {t('locations.deliveryZones')}
            </a>
          </div>
        )}
      </div>

      {actionError && <div className="form-error-banner">{actionError}</div>}

      {canManage && location.status !== 'ARCHIVED' && (
        <div className="settings-card lifecycle-card">
          <h3>{t('menu.lifecycle')}</h3>
          <LifecycleActions
            status={location.status as 'ACTIVE' | 'SUSPENDED' | 'ARCHIVED'}
            entityName={location.name}
            disabled={!canManage}
            onAction={handleLifecycle}
          />
        </div>
      )}
    </section>
  )
}